import { Directive, HostListener, ElementRef, Input } from '@angular/core';

@Directive({
  selector: 'input[type="number"][appBlockNumberSpecialchars]',
  standalone: false
})
export class BlockNumberSpecialcharsDirective {
  /**
   * Input to customize which base characters to block.
   * Defaults to blocking all 4 characters: 'e', 'E', '+', '-'
   */
  @Input() blockChars: string[] = ['e', 'E', '+', '-'];

  /**
   * Input to supply additional characters to block alongside the base blockChars.
   * Usage example: [extraChars]="['.']"
   */
  @Input() extraChars: string[] = [];

  constructor(private readonly el: ElementRef) {}

  /**
   * Gets the combined list of all blocked characters (blockChars + extraChars)
   */
  get allBlockedChars(): string[] {
    return [...this.blockChars, ...this.extraChars];
  }

  /**
   * WAY 1: Keydown Event
   * Blocks typing the configured characters directly.
   */
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.allBlockedChars.includes(event.key)) {
      event.preventDefault();
    }
  }

  /**
   * WAY 2: Input Event
   * Sanitizes value and updates the Form Model.
   */
  @HostListener('input', ['$event'])
  onInputChange(event: Event): void {
    this.sanitize();
  }

  /**
   * WAY 3: Blur Event
   * Cleans up the value when user leaves focus from the input.
   */
  @HostListener('blur', ['$event'])
  onBlur(event: Event): void {
    this.sanitize();
  }

  /**
   * WAY 4: Paste Event
   * Prevents pasting text containing any of the blocked characters.
   */
  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData;
    if (clipboardData) {
      const pastedText = clipboardData.getData('text');
      
      // Check if the pasted text contains any blocked characters
      const hasBlockedChar = pastedText.split('').some(char => this.allBlockedChars.includes(char));

      if (hasBlockedChar) {
        event.preventDefault();

        // Strip the blocked characters from the pasted text
        const sanitized = this.removeBlockedChars(pastedText);
        const input = this.el.nativeElement;

        // Merge with current selection
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const currentVal = input.value;
        const newVal = currentVal.substring(0, start) + sanitized + currentVal.substring(end);

        input.value = newVal;
        this.sanitize();
      }
    }
  }

  /**
   * WAY 5: Drop Event
   * Prevents dragging and dropping text containing any of the blocked characters.
   */
  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    const dataTransfer = event.dataTransfer;
    if (dataTransfer) {
      const droppedText = dataTransfer.getData('text');
      const hasBlockedChar = droppedText.split('').some(char => this.allBlockedChars.includes(char));
      
      if (hasBlockedChar) {
        event.preventDefault();
      }
    }
  }

  /**
   * Helper function to remove only the configured blocked characters from a string
   */
  private removeBlockedChars(val: string): string {
    return val.split('').filter(char => !this.allBlockedChars.includes(char)).join('');
  }

  /**
   * Helper function to sanitize the native input value and trigger Angular updates
   */
  private sanitize(): void {
    const input = this.el.nativeElement;
    const originalValue = input.value;
    const sanitized = this.removeBlockedChars(originalValue);

    if (originalValue !== sanitized) {
      input.value = sanitized;
      // Dispatch input event to notify NgModel/FormControl
      const newEvent = new Event('input', { bubbles: true });
      input.dispatchEvent(newEvent);
    }
  }
}
